import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useClienteRecomendaciones } from '../../../hooks/useClienteRecomendaciones';
import { useAdminClientesAsesoria } from '../../../hooks/useAdminClientesAsesoria';
import { mockRecomendaciones } from '../../../mocks/clienteArmario';
import type { Recomendacion } from '../../../hooks/useClienteRecomendaciones';

const PAGE_SIZE = 10;
const TIPOS: { value: Recomendacion['tipo']; label: string }[] = [
  { value: 'combinacion', label: 'Combinación' },
  { value: 'alerta', label: 'Alerta' },
  { value: 'sugerencia', label: 'Sugerencia' },
];

interface Props {
  selectedClienteId?: number;
}

export default function AdminAsesoriaRecomendaciones({ selectedClienteId }: Props) {
  const { data: clientesData } = useAdminClientesAsesoria();
  const [selectedCliente, setSelectedCliente] = useState<number>(selectedClienteId || 0);
  const [clientes, setClientes] = useState(clientesData);

  useEffect(() => {
    setClientes(clientesData);
    if (selectedClienteId && selectedClienteId > 0) {
      setSelectedCliente(selectedClienteId);
    } else if (clientesData.length > 0 && selectedCliente === 0) {
      setSelectedCliente(clientesData[0].id);
    }
  }, [clientesData, selectedClienteId, selectedCliente]);

  const recomendacionesCliente = mockRecomendaciones.filter((r) => r.cliente_id === selectedCliente);

  const {
    data,
    setData,
    markAsRead,
    refresh,
    loading,
  } = useClienteRecomendaciones(recomendacionesCliente, selectedCliente || undefined);

  const [editing, setEditing] = useState<Recomendacion | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState<string>('todas');
  const [formError, setFormError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);

  const filtered = data.filter(
    (r) =>
      (tipoFilter === 'todas' || r.tipo === tipoFilter) &&
      (r.titulo.toLowerCase().includes(search.toLowerCase()) ||
        r.descripcion.toLowerCase().includes(search.toLowerCase()))
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_SIZE;
  const paged = filtered.slice(startIdx, startIdx + PAGE_SIZE);

  const currentCliente = clientes.find((c) => c.id === selectedCliente);

  const handleAdd = async (formData: FormData) => {
    setFormError(null);
    const tipo = (formData.get('tipo') as Recomendacion['tipo']) || 'sugerencia';
    const titulo = (formData.get('titulo') as string)?.trim();
    const descripcion = (formData.get('descripcion') as string)?.trim();
    const fecha = (formData.get('fecha') as string) || new Date().toISOString().split('T')[0];

    if (!titulo || !descripcion) {
      setFormError('Título y descripción son obligatorios.');
      return;
    }

    setIsAdding(false);
    try {
      const { data: inserted, error: err } = await supabase
        .from('cliente_recomendaciones')
        .insert({ cliente_id: selectedCliente, tipo, titulo, descripcion, fecha, leida: false })
        .select()
        .single();
      if (err) {
        setFormError(err.message);
        setIsAdding(true);
        return;
      }
      if (inserted) {
        setData((prev) => [inserted as Recomendacion, ...prev]);
      }
    } catch {
      setFormError('Error al crear recomendación');
      setIsAdding(true);
    }
  };

  const handleUpdate = async (formData: FormData) => {
    if (!editing) return;
    setFormError(null);
    const tipo = (formData.get('tipo') as Recomendacion['tipo']) || 'sugerencia';
    const titulo = (formData.get('titulo') as string)?.trim();
    const descripcion = (formData.get('descripcion') as string)?.trim();
    const fecha = (formData.get('fecha') as string) || editing.fecha;

    if (!titulo || !descripcion) {
      setFormError('Título y descripción son obligatorios.');
      return;
    }

    const prevEditing = editing;
    setEditing(null);
    try {
      const { data: updated, error: err } = await supabase
        .from('cliente_recomendaciones')
        .update({ tipo, titulo, descripcion, fecha })
        .eq('id', prevEditing.id)
        .select()
        .single();
      if (err) {
        setFormError(err.message);
        setEditing(prevEditing);
        return;
      }
      if (updated) {
        setData((prev) =>
          prev.map((item) => (item.id === prevEditing.id ? (updated as Recomendacion) : item))
        );
      }
    } catch {
      setFormError('Error al actualizar');
      setEditing(prevEditing);
    }
  };

  const handleRemove = async (id: number) => {
    if (!confirm('¿Eliminar esta recomendación?')) return;
    try {
      const { error: err } = await supabase
        .from('cliente_recomendaciones')
        .delete()
        .eq('id', id);
      if (err) {
        setFormError(err.message);
        return;
      }
      setData((prev) => prev.filter((item) => item.id !== id));
    } catch {
      setFormError('Error al eliminar');
    }
  };

  const moveItem = useCallback(
    async (fromId: number, toId: number) => {
      if (fromId === toId) return;
      const sorted = [...data].sort(
        (a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      );
      const fromIndex = sorted.findIndex((d) => d.id === fromId);
      const toIndex = sorted.findIndex((d) => d.id === toId);
      if (fromIndex === -1 || toIndex === -1) return;
      sorted.splice(fromIndex, 1);
      sorted.splice(toIndex, 0, data.find((d) => d.id === fromId)!);
      const reordered = sorted.map((item, idx) => ({
        ...item,
        sort_order: idx,
      }));
      setData(reordered);
    },
    [data, setData]
  );

  const handleDragStart = (
    e: React.DragEvent<HTMLTableRowElement>,
    id: number
  ) => {
    setDraggingId(id);
    e.dataTransfer.setData('text/plain', String(id));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLTableRowElement>,
    id: number
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== id) setDragOverId(id);
  };

  const handleDrop = (
    e: React.DragEvent<HTMLTableRowElement>,
    toId: number
  ) => {
    e.preventDefault();
    const fromId = Number(e.dataTransfer.getData('text/plain'));
    moveItem(fromId, toId);
    setDragOverId(null);
    setDraggingId(null);
  };

  const handleDragEnd = () => {
    setDragOverId(null);
    setDraggingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header con selector de cliente */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <h2 className="font-serif text-brand-black text-2xl font-medium">
            Recomendaciones
          </h2>
          <div className="relative">
            <select
              value={selectedCliente}
              onChange={(e) => {
                setSelectedCliente(Number(e.target.value));
                setPage(1);
              }}
              className="appearance-none bg-white border border-brand-gray-200 rounded-lg pl-4 pr-10 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black cursor-pointer"
            >
              {clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-brand-gray-400">
              <i className="ri-arrow-down-s-line" />
            </div>
          </div>
          {currentCliente && (
            <span className="inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium bg-brand-sage/20 text-brand-black">
              {currentCliente.plan_contratado || 'Sin plan'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <select
            value={tipoFilter}
            onChange={(e) => {
              setTipoFilter(e.target.value);
              setPage(1);
            }}
            className="bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          >
            <option value="todas">Todos los tipos</option>
            {TIPOS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-64 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          />
          <button
            onClick={() => {
              setIsAdding(true);
              setEditing(null);
              setFormError(null);
            }}
            className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
          >
            + Nueva
          </button>
          <button
            onClick={refresh}
            className="w-10 h-10 rounded-lg bg-white border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:text-brand-black hover:border-brand-black transition-colors"
            title="Recargar"
          >
            <i className="ri-refresh-line" />
          </button>
        </div>
      </div>

      {currentCliente && (
        <div className="bg-brand-sage/10 border border-brand-sage/30 rounded-xl px-5 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-brand-sage/30 flex items-center justify-center text-brand-black">
            <i className="ri-user-line" />
          </div>
          <div>
            <p className="font-sans text-brand-black text-sm font-medium">{currentCliente.nombre}</p>
            <p className="font-sans text-brand-gray-600 text-xs">
              {data.length} recomendaciones · {currentCliente.email}
            </p>
          </div>
        </div>
      )}

      {formError && !isAdding && !editing && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
          <i className="ri-error-warning-line text-red-500 mt-0.5" />
          <p className="font-sans text-red-600 text-sm">{formError}</p>
        </div>
      )}

      {/* Formulario */}
      {(isAdding || editing) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            if (editing) handleUpdate(fd);
            else handleAdd(fd);
          }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-5"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Recomendación' : 'Nueva Recomendación'}
            </h3>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
                setFormError(null);
              }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 mt-0.5" />
              <p className="font-sans text-red-600 text-sm">{formError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Tipo *
              </label>
              <select
                name="tipo"
                defaultValue={editing?.tipo ?? 'sugerencia'}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black bg-white"
              >
                {TIPOS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Fecha *
              </label>
              <input
                name="fecha"
                type="date"
                defaultValue={editing?.fecha ?? new Date().toISOString().split('T')[0]}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
                Título *
              </label>
              <input
                name="titulo"
                defaultValue={editing?.titulo ?? ''}
                required
                className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
              />
            </div>
          </div>

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">
              Descripción *
            </label>
            <textarea
              name="descripcion"
              defaultValue={editing?.descripcion ?? ''}
              rows={4}
              required
              className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black resize-none"
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={loading}
              className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap disabled:opacity-60"
            >
              {loading ? 'Guardando...' : 'Guardar'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setIsAdding(false);
                setFormError(null);
              }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {/* Tabla */}
      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-gray-50">
              <tr>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3 w-10">
                  Orden
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Tipo
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Título
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Estado
                </th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Fecha
                </th>
                <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray-200">
              {paged.map((rec) => (
                <tr
                  key={rec.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, rec.id)}
                  onDragOver={(e) => handleDragOver(e, rec.id)}
                  onDrop={(e) => handleDrop(e, rec.id)}
                  onDragEnd={handleDragEnd}
                  className={`hover:bg-brand-gray-50/50 transition-colors cursor-move ${
                    draggingId === rec.id ? 'opacity-40' : ''
                  } ${dragOverId === rec.id ? 'bg-brand-sage/20' : ''}`}
                >
                  <td className="px-5 py-4">
                    <div className="w-6 h-6 flex items-center justify-center text-brand-gray-400">
                      <i className="ri-draggable text-lg" />
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={`inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium ${
                        rec.tipo === 'combinacion'
                          ? 'bg-brand-sage/20 text-brand-black'
                          : rec.tipo === 'alerta'
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-sky-50 text-sky-700'
                      }`}
                    >
                      {TIPOS.find((t) => t.value === rec.tipo)?.label || rec.tipo}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-sans text-brand-black text-sm font-medium line-clamp-1 max-w-[300px]">
                      {rec.titulo}
                    </p>
                    <p className="font-sans text-brand-gray-400 text-xs mt-0.5 line-clamp-1 max-w-[300px]">
                      {rec.descripcion}
                    </p>
                  </td>
                  <td className="px-5 py-4">
                    {rec.leida ? (
                      <span className="font-sans text-brand-gray-400 text-xs">Leída</span>
                    ) : (
                      <span className="font-sans text-brand-sage text-xs font-medium">
                        Nueva
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">
                    {new Date(rec.fecha).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {!rec.leida && (
                        <button
                          onClick={() => markAsRead(rec.id)}
                          className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-sage hover:text-white transition-colors"
                          title="Marcar como leída"
                        >
                          <i className="ri-check-line" />
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setEditing(rec);
                          setIsAdding(false);
                          setFormError(null);
                        }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={() => handleRemove(rec.id)}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-10 text-center">
            <p className="font-sans text-brand-gray-400 text-sm">
              No se encontraron recomendaciones
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white border border-brand-gray-200 rounded-2xl px-5 py-3">
          <div className="font-sans text-brand-gray-500 text-sm">
            Mostrando {startIdx + 1}–
            {Math.min(startIdx + PAGE_SIZE, filtered.length)} de {filtered.length}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40"
            >
              <i className="ri-arrow-left-s-line" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`w-8 h-8 rounded-lg font-sans text-sm flex items-center justify-center transition-colors ${
                  p === safePage
                    ? 'bg-brand-black text-white'
                    : 'border border-brand-gray-200 text-brand-gray-600 hover:border-brand-black hover:text-brand-black'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="w-8 h-8 rounded-lg border border-brand-gray-200 flex items-center justify-center text-brand-gray-600 hover:border-brand-black hover:text-brand-black transition-colors disabled:opacity-40"
            >
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
